/**
 * The conversion signal DE publishes when a lead submission succeeds.
 *
 * DE must not depend on any analytics package, so it announces the conversion
 * on the window rather than calling a vendor. Hosts subscribe and decide what
 * to send. The name and payload shape live here so publisher and subscriber
 * share one definition instead of two copies of a string literal.
 */
export const DE_CONVERSION_EVENT = 'de:conversion';

export interface DynamicExperienceConversion {
  readonly name: 'Lead';
  readonly pageUrl: string;
}

export type DynamicExperienceConversionEvent =
  CustomEvent<DynamicExperienceConversion>;

/**
 * Announces a conversion to any subscribed host.
 *
 * Dispatch is synchronous, so a subscriber runs before the caller continues to
 * result navigation.
 */
export function emitConversion(conversion: DynamicExperienceConversion): void {
  if (typeof window === 'undefined') {
    return;
  }

  window.dispatchEvent(
    new CustomEvent<DynamicExperienceConversion>(DE_CONVERSION_EVENT, {
      detail: conversion,
    }),
  );
}

/**
 * Subscribes to DE conversions.
 *
 * @returns Unsubscribe function. Safe to call on a server render, where it is a
 * no-op, so callers can use it in an effect cleanup without a window guard.
 */
export function subscribeToConversions(
  listener: (conversion: DynamicExperienceConversion) => void,
): () => void {
  if (typeof window === 'undefined') {
    return () => undefined;
  }

  const handler = (event: Event): void => {
    listener((event as DynamicExperienceConversionEvent).detail);
  };

  window.addEventListener(DE_CONVERSION_EVENT, handler);
  return () => window.removeEventListener(DE_CONVERSION_EVENT, handler);
}
